<template>
  <div class="sidebar" :class="{ 'sidebar-collapsed': isCollapsed }" data-tour="sidebar">
    <div class="logo-container">
      <div class="logo-wrapper" v-if="!isCollapsed">
        <img src="@/assets/images/trazalogis-logo.svg" alt="Trazalogis" class="logo-image" />
        <h1 class="logo-text">Trazalogis</h1>
      </div>
      <div class="logo-icon-wrapper" v-else>
        <img src="@/assets/images/trazalogis-icon.svg" alt="T" class="logo-icon-image" />
      </div>
    </div>
    
    <!-- Información del usuario -->
    <div class="user-info" v-if="!isCollapsed && currentUser">
      <div class="user-avatar">
        <i class="bi bi-person-circle"></i>
      </div>
      <div class="user-details">
        <div class="user-name">{{ currentUser.username }}</div>
        <div class="user-role">{{ getRoleText(currentUser.role) }}</div>
      </div>
    </div>

    <!-- Configuración del producto por dos ejes (solo admin) -->
    <div class="edition-switch" v-if="!isCollapsed && isAdmin">
      <span class="edition-label">Configuración</span>
      <label class="edition-toggle">
        <input
          type="checkbox"
          :checked="manejaPrecios"
          @change="toggleFlag('manejaPrecios', $event.target.checked)"
        />
        <span>Maneja precios</span>
      </label>
      <label class="edition-toggle">
        <input
          type="checkbox"
          :checked="pickingConOperarios"
          @change="toggleFlag('pickingConOperarios', $event.target.checked)"
        />
        <span>Operarios de bodega</span>
      </label>
      <label class="edition-toggle">
        <input
          type="checkbox"
          :checked="sectorSalud"
          @change="toggleFlag('sectorSalud', $event.target.checked)"
        />
        <span>Institución de salud</span>
      </label>
    </div>

    <nav>
      <ul>
        <!-- Menú para OPERARIOS -->
        <template v-if="isOperator">
          <li>
            <router-link to="/operator" class="nav-link" :title="isCollapsed ? 'Mi Dashboard' : ''" data-tour="operator-dashboard" @click="handleNavClick">
              <i class="bi bi-speedometer2"></i> <span v-if="!isCollapsed">Mi Dashboard</span>
            </router-link>
          </li>
          <li>
            <router-link to="/inventario" class="nav-link" :title="isCollapsed ? 'Consultar Inventario' : ''" data-tour="inventory-link" @click="handleNavClick">
              <i class="bi bi-list-check"></i> <span v-if="!isCollapsed">Consultar Inventario</span>
            </router-link>
          </li>
        </template>

        <!-- Menú principal filtrado por edición (Pyme/Empresa) Y permisos del rol.
             Aplica a todos los roles salvo el operario legacy (que tiene su dashboard). -->
        <template v-if="!isOperator">
          <li v-for="item in visibleAdminItems" :key="item.to">
            <router-link :to="item.to" class="nav-link" :title="isCollapsed ? menuLabel(item) : ''" :data-tour="item.dataTour" @click="handleNavClick">
              <i :class="item.icon"></i> <span v-if="!isCollapsed">{{ menuLabel(item) }}</span>
              <span v-if="item.module === 'cumplimiento' && alertCount" class="alert-badge" :title="alertCount + ' alerta(s) vigente(s)'">{{ alertCount }}</span>
            </router-link>
          </li>
        </template>
      </ul>
    </nav>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import apiClient from '@/api/index.js'
import { EDITIONS } from '@/config/editions'

// Menú de administrador. Cada item declara su `module` para poder filtrarlo
// según la edición activa (Pyme oculta los módulos marcados como avanzados).
// Cada item declara `module` (visibilidad por edición Pyme/Empresa) y `perms`
// (capacidades que dan acceso; se muestra si el usuario tiene AL MENOS UNA).
const ADMIN_MENU = [
  { to: '/analytics', label: 'Análisis', icon: 'bi bi-graph-up', dataTour: 'analytics-link', module: 'analytics', perms: ['analytics:view'] },
  { to: '/orders', label: 'Gestión de Órdenes', icon: 'bi bi-cart-check', dataTour: 'orders-link', module: 'orders', perms: ['orders:view', 'orders:create', 'orders:manage'] },
  { to: '/intelligent-picking', label: 'Picking Inteligente', icon: 'bi bi-cpu', dataTour: 'intelligent-picking-link', module: 'intelligent-picking', perms: ['picking:execute', 'picking:assign'] },
  { to: '/service-assignment', label: 'Asignación de Servicios', icon: 'bi bi-clipboard-check', dataTour: 'service-assignment-link', module: 'service-assignment', perms: ['picking:assign'] },
  { to: '/cost-management', label: 'Gestión de Costos', icon: 'bi bi-currency-dollar', dataTour: 'cost-management-link', module: 'cost-management', perms: ['costs:view'] },
  { to: '/instalaciones', label: 'Instalaciones', icon: 'bi bi-building', dataTour: 'installations-link', module: 'instalaciones', perms: ['installations:manage'] },
  { to: '/ingresos', label: 'Ingresos', icon: 'bi bi-box-arrow-in-down', dataTour: 'ingresos-link', module: 'ingresos', perms: ['reception:count', 'reception:create'] },
  { to: '/recepciones', label: 'Recepciones', icon: 'bi bi-clipboard-data', dataTour: 'recepciones-link', module: 'recepciones', perms: ['reception:count', 'reception:create'] },
  { to: '/documentos-dte', label: 'Documentos DTE', icon: 'bi bi-file-earmark-code', dataTour: 'documentos-dte-link', module: 'documentos-dte', perms: ['dte:manage'] },
  { to: '/proveedores', label: 'Proveedores', icon: 'bi bi-truck-front', dataTour: 'proveedores-link', module: 'proveedores', perms: ['reception:create', 'dte:manage'] },
  { to: '/egresos', label: 'Egresos', icon: 'bi bi-box-arrow-in-up', dataTour: 'egresos-link', module: 'egresos', perms: ['egress:manage', 'egress:execute'] },
  { to: '/despachos', label: 'Despachos', icon: 'bi bi-truck', dataTour: 'despachos-link', module: 'despachos', perms: ['egress:manage'] },
  { to: '/movimientos-internos', label: 'Movimientos Internos', icon: 'bi bi-arrow-left-right', dataTour: 'movimientos-internos-link', module: 'movimientos-internos', perms: ['inventory:manage', 'movements:execute_assigned'] },
  { to: '/mi-servicio', label: 'Mi servicio', icon: 'bi bi-hospital', dataTour: 'mi-servicio-link', module: 'mi-servicio', perms: ['dm:receive_destination', 'dm:custody', 'dm:clinical_use'] },
  { to: '/destinos', label: 'Destinos internos', icon: 'bi bi-diagram-3', dataTour: 'destinos-link', module: 'destinos', perms: ['dm:config'] },
  { to: '/consignaciones', label: 'Consignaciones y pabellón', icon: 'bi bi-box-seam', dataTour: 'consignaciones-link', module: 'consignaciones', perms: ['consignment:manage', 'dm:audit_view'] },
  { to: '/cumplimiento', label: 'Cumplimiento', icon: 'bi bi-shield-check', dataTour: 'cumplimiento-link', module: 'cumplimiento', perms: ['dm:audit_view', 'dm:recall', 'dm:simulation', 'dm:config'] },
  { to: '/trazabilidad-dm', label: 'Trazabilidad DM', icon: 'bi bi-heart-pulse', dataTour: 'trazabilidad-dm-link', module: 'trazabilidad-dm', perms: ['dm:view'] },
  { to: '/trazabilidad', label: 'Gestión de Trazabilidad', icon: 'bi bi-geo-alt', dataTour: 'trazabilidad-link', module: 'trazabilidad', perms: ['traceability:view'] },
  { to: '/inventario', label: 'Gestión de Inventario', icon: 'bi bi-list-check', dataTour: 'inventory-link', module: 'inventario', perms: ['inventory:view', 'inventory:view_available', 'inventory:manage'] },
  { to: '/users', label: 'Gestión de Usuarios', icon: 'bi bi-people', dataTour: 'users-link', module: 'users', perms: ['users:manage'] },
  { to: '/auditoria', label: 'Auditoría', icon: 'bi bi-journal-check', dataTour: 'auditoria-link', module: 'auditoria', perms: ['dm:audit_view', 'users:manage'] },
  { to: '/empresa', label: 'Configuraciones', icon: 'bi bi-gear', dataTour: 'empresa-link', module: 'empresa', perms: ['installations:manage'] }
]

export default {
  name: 'SideBar',
  data() {
    return { alertCount: 0, alertTimer: null }
  },
  mounted() {
    this.loadAlertCount()
    this.alertTimer = setInterval(() => this.loadAlertCount(), 300000)
  },
  beforeUnmount() { clearInterval(this.alertTimer) },
  watch: {
    sectorSalud() { this.loadAlertCount() },
    isAuthenticated() { this.loadAlertCount() }
  },
  props: {
    isCollapsed: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters('auth', ['currentUser', 'isAuthenticated', 'hasPermission']),
    ...mapGetters('edition', ['edition', 'isModuleVisible', 'manejaPrecios', 'pickingConOperarios', 'sectorSalud', 'vocab']),

    isAdmin() {
      return this.currentUser?.role === 'admin'
    },

    // Operario legacy (rol 'operator') conserva su dashboard propio
    isOperator() {
      return this.currentUser?.role === 'operator'
    },

    isMobile() {
      return window.innerWidth <= 768
    },

    editionOptions() {
      return Object.values(EDITIONS)
    },

    // Items visibles según edición (Pyme/Empresa) Y permisos del rol
    visibleAdminItems() {
      return ADMIN_MENU.filter(item =>
        this.isModuleVisible(item.module) &&
        item.perms.some(p => this.hasPermission(p))
      )
    }
  },

  methods: {
    async loadAlertCount() {
      if (!this.isAuthenticated || !this.sectorSalud || !this.hasPermission('dm:view')) { this.alertCount = 0; return }
      try { const { data } = await apiClient.get('/dm/alerts/count'); this.alertCount = data.count || 0 } catch (e) { this.alertCount = 0 }
    },

    changeEdition(edition) {
      this.$store.commit('edition/setEdition', edition)
    },

    toggleFlag(key, value) {
      this.$store.commit('edition/setFlag', { key, value })
    },

    // Vocabulario por sector: en salud "Egresos" son "Salidas a servicios"
    menuLabel(item) {
      if (this.sectorSalud && item.module === 'egresos') return this.vocab('egresos')
      return item.label
    },

    getRoleText(role) {
      const roleTexts = {
        admin: 'Administrador',
        warehouse_manager: 'Jefe de Bodega',
        receiving_operator: 'Operario de Recepción',
        picker: 'Picker',
        salesperson: 'Vendedor',
        traceability_officer: 'Responsable de trazabilidad DM',
        destination_manager: 'Encargado de destino',
        clinical_professional: 'Profesional clínico',
        quality_auditor: 'Auditor de calidad',
        // legacy
        operator: 'Operario',
        viewer: 'Visualizador',
        user: 'Usuario'
      }
      return roleTexts[role] || role
    },

    handleNavClick() {
      // Solo cerrar el sidebar en móvil cuando se hace clic en un enlace de navegación
      if (this.isMobile && !this.isCollapsed) {
        this.$emit('close-sidebar')
      }
    }
  }
}
</script>

<style scoped>
.alert-badge { background: #ef6c00; color: #fff; border-radius: 10px; font-size: 11px; padding: 0 6px; margin-left: 6px; font-weight: 600; }
.sidebar {
  background-color: #1e2a3a;
  color: white;
  height: 100vh;
  width: 250px;
  position: fixed;
  left: 0;
  top: 0;
  padding-top: 20px;
  z-index: 1000;
  overflow-y: auto;
  transition: width 0.3s ease;
}

.sidebar-collapsed {
  width: 60px;
  overflow-x: hidden;
}

.logo-container {
  text-align: center;
  margin-bottom: 20px;
  padding: 0 10px;
}

.logo-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  gap: 12px;
}

.logo-image {
  width: 50px;
  height: 50px;
  object-fit: contain;
}

.logo-text {
  color: #41B883;
  font-size: 22px;
  font-weight: bold;
  margin: 0;
}

.logo-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-icon-image {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

/* Información del usuario */
.user-info {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  margin-bottom: 20px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-left: 10px;
  margin-right: 10px;
}

.user-avatar {
  margin-right: 12px;
}

.user-avatar i {
  font-size: 2rem;
  color: #41B883;
}

.user-details {
  flex: 1;
}

.user-name {
  font-weight: bold;
  font-size: 0.9rem;
  margin-bottom: 2px;
}

.user-role {
  font-size: 0.75rem;
  color: #b0b0b0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Selector de edición Pyme/Empresa */
.edition-switch {
  margin: 0 10px 16px;
  padding: 10px 12px;
  background-color: rgba(255, 255, 255, 0.06);
  border-radius: 8px;
}

.edition-label {
  display: block;
  font-size: 0.7rem;
  color: #b0b0b0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.edition-buttons {
  display: flex;
  gap: 6px;
}

.edition-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: #e0e0e0;
  cursor: pointer;
  padding: 3px 0;
}

.edition-toggle input {
  accent-color: #41B883;
  cursor: pointer;
}

.edition-btn {
  flex: 1;
  padding: 5px 8px;
  font-size: 0.78rem;
  color: #e0e0e0;
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.edition-btn:hover {
  border-color: #41B883;
  color: #41B883;
}

.edition-btn.active {
  background-color: #41B883;
  border-color: #41B883;
  color: white;
  font-weight: bold;
}

nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

nav li {
  margin-bottom: 5px;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  color: #e0e0e0;
  text-decoration: none;
  transition: all 0.3s;
  white-space: nowrap;
  border-radius: 0 25px 25px 0;
  margin-right: 10px;
}

.sidebar-collapsed .nav-link {
  justify-content: center;
  padding: 12px;
  margin-right: 0;
  border-radius: 0;
}

.nav-link:hover {
  background-color: rgba(65, 184, 131, 0.1);
  color: #41B883;
  transform: translateX(5px);
}

.router-link-active {
  background-color: #41B883;
  color: white;
  font-weight: bold;
}

.router-link-active:hover {
  background-color: #369870;
  transform: translateX(0);
}

.nav-link i {
  margin-right: 12px;
  font-size: 1.1rem;
  width: 20px;
  text-align: center;
}

.sidebar-collapsed .nav-link i {
  margin-right: 0;
  font-size: 1.3rem;
}

/* Estilos responsivos para dispositivos móviles */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    width: 250px;
    z-index: 1001;
  }
  
  .sidebar.sidebar-mobile-open {
    transform: translateX(0);
  }
  
  .sidebar.sidebar-mobile-closed {
    transform: translateX(-100%);
  }
  
  .sidebar-collapsed {
    transform: translateX(-100%);
  }
  
  /* En móvil, no mostrar la información del usuario cuando está colapsado */
  .sidebar-collapsed .user-info {
    display: none;
  }
  
  /* Ajustar navegación en móvil */
  .nav-link {
    padding: 15px 20px;
    font-size: 1rem;
  }
  
  .nav-link i {
    margin-right: 15px;
    font-size: 1.2rem;
  }
}

@media (max-width: 480px) {
  .sidebar {
    width: 280px;
  }
  
  .logo-text {
    font-size: 20px;
  }
  
  .user-name {
    font-size: 0.85rem;
  }
  
  .user-role {
    font-size: 0.7rem;
  }
}
</style>
